/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { ProductWarehouseInventoryAvailability } from '@msdyn365-commerce/retail-proxy';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductAvailableQuantity, ProductType, ReleasedProductType } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { getSelectedProductIdFromActionInput, getSelectedVariant, IProductInventoryInformation, SelectedVariantInput } from '.';
import { ArrayExtensions } from './utilities';
import { DeliveryMode } from './utilities/product-inventory-information';
import { createInventoryAvailabilitySearchCriteria, mapAggregatedProductInventoryInformation } from './utilities/product-inventory-utils';

/**
 * Input class for the getProductAvailabilitiesForSelectedVariant Data Action.
 */
export class ProductAvailabilitiesForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    constructor(productId: number | string, channelId: number) {
        this.productId = typeof productId === 'string' ? +productId : productId;
        this.channelId = channelId;
    }

    public getCacheKey = () => 'ProductAvailabilitiesForSelectedVariant';

    public getCacheObjectType = () => 'ProductAvailabilities';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getProductAvailabilitiesForSelectedVariant data action.
 * @param inputData The input data passed to the createInput method.
 */
export const createProductAvailabilitiesForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): ProductAvailabilitiesForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new ProductAvailabilitiesForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create ProductAvailabilitiesForSelectedVariantInput, no productId found on module config or query');

};

/**
 * The action method for the getProductAvailabilitiesForSelectedVariant data action.
 * @param input
 * @param ctx
 */
export async function getProductAvailabilitiesForSelectedVariantAction(
    input: ProductAvailabilitiesForSelectedVariantInput,
    ctx: IActionContext
): Promise<IProductInventoryInformation[] | undefined> {
    const selectedVariantInput = new SelectedVariantInput(input.productId, input.channelId);
    const productInventoryInformation: IProductInventoryInformation[] = [];
    const PRODUCTASSERVICE = 2 as ReleasedProductType.Service;

    return getSelectedVariant(selectedVariantInput, ctx)
        .then(productResult => {
            // Don't get availability if it is service item
            if (productResult && productResult.ItemTypeValue !== PRODUCTASSERVICE && productResult.ProductTypeValue !== ProductType.Master) {
                const shippingSearchCriteria = createInventoryAvailabilitySearchCriteria(
                    ctx,
                    [productResult.RecordId],
                    true,
                    undefined,
                    undefined,
                    DeliveryMode.shipping);
                const pickupSearchCriteria = createInventoryAvailabilitySearchCriteria(ctx, [input.productId], false, true, undefined, DeliveryMode.pickup);
                return getEstimatedAvailabilityAsync(
                    { callerContext: ctx },
                    shippingSearchCriteria
                ).then(response => {
                    if (response.ProductWarehouseInventoryAvailabilities) {
                        const shippingInventory = mapAggregatedProductInventoryInformation(ctx, response);
                        if (ArrayExtensions.hasElements(shippingInventory)) {
                            shippingInventory[0].deliveryType = DeliveryMode.shipping;
                            productInventoryInformation.push(shippingInventory[0]);
                        }
                    }
                    return getEstimatedAvailabilityAsync({ callerContext: ctx }, pickupSearchCriteria).then(pickupResponse => {
                        if (pickupResponse.ProductWarehouseInventoryAvailabilities) {
                            const pickupInventory = mapAggregatedProductInventoryInformation(ctx, pickupResponse);
                            if (ArrayExtensions.hasElements(pickupInventory)) {
                                pickupInventory[0].deliveryType = DeliveryMode.pickup;
                                productInventoryInformation.push(pickupInventory[0]);
                            }
                        }
                        return productInventoryInformation;
                    });
                });
            }

            return undefined;
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getPriceForSelectedVariantAction]Error executing action');
            throw new Error('[getPriceForSelectedVariantAction]Error executing action');
        });
}

/**
 * The function that maps a ProductWareHouse object into a ProductAvailabilityQuantity.
 * @param productsWarehouseInventory
 */
export function mergeProductWarehouseToProductAvailabities(productsWarehouseInventory: ProductWarehouseInventoryAvailability[]): ProductAvailableQuantity[] {
    const productAvailable: ProductAvailableQuantity[] = [];
    if (productsWarehouseInventory && productsWarehouseInventory.length > 0) {
        for (const product of productsWarehouseInventory) {
            if (product.TotalAvailable !== undefined && product.ProductId !== undefined) {
                productAvailable.push({ ProductId: product.ProductId, AvailableQuantity: product.TotalAvailable });
            }
        }
    }
    return productAvailable;
}

/**
 * The complete getProductAvailabilitiesForSelectedVariant data action
 * Get the currently selected variant via the getSelectedVariant data action, and
 * then gets the availabilities for the variant via the getProductAvailabilities RetailServer API.
 */
export const getProductAvailabilitiesForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-availabilities-for-selected-variant',
    action: <IAction<IProductInventoryInformation[] | null>>getProductAvailabilitiesForSelectedVariantAction,
    input: createProductAvailabilitiesForSelectedVariantInput
});

export default getProductAvailabilitiesForSelectedVariantActionDataAction;
